'use client';

import { useRef, useState, useEffect } from 'react';
import { detectLanguage } from '../../services/detectLanguage';
import { useTranslations, useLocale } from 'next-intl';
import { useOfferedTemplates, templateTitle } from '@/contexts/templates/hooks/useOfferedTemplates';
import { SEED_STORY_IDEAS as IDEA_SEEDS } from '@/contexts/templates/seed/storyIdeasSeed';
import { useWizardIdeas } from '@/contexts/templates/hooks/useWizardIdeas';
import { ChevronDown, Dices, Sparkles } from 'lucide-react';
import { Label } from '@/shared/components/ui/label';
import { Input } from '@/shared/components/ui/input';
import { Textarea } from '@/shared/components/ui/textarea';
import { Slider } from '@/shared/components/ui/slider';
import { Button } from '@/shared/components/ui/button';
import { Checkbox } from '@/shared/components/ui/checkbox';
import { CheckboxField } from '@/shared/components/ui/checkbox-field';
import { Card, CardContent } from '@/shared/components/ui/card';
import { cn } from '@/shared/utils/cn';
import { Tabs, TabsContent } from '@/shared/components/ui/tabs';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/shared/components/ui/collapsible';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select';
import {
  CharacterSelector,
  type CharacterAssignment,
} from '@/contexts/characters/components';
import type { CharacterSelectorHandle } from '@/contexts/characters';
import { useCharacters } from '@/contexts/characters';
import { createPhotoHero, renamePhotoHero } from '@/contexts/characters/actions/photoHero';
import { withPhotoHero, withPhotoHeroName, withoutPhotoHero } from './PhotoHeroes';
import { ReplacePicker } from './ReplacePicker';
import { PHOTO_CONSENT_VERSION } from '@/contexts/characters/photoConsent';
import { fileToBase64 } from '@/contexts/characters/utils/fileToBase64';
import { PhotoConsentCheckbox } from '@/contexts/characters';
import { useCurrentUser } from '@/contexts/authentication/client';
import { VoiceToPromptButton } from '@/contexts/ai/components';
// Story-subject read seam (A2): behind IDENTITY_SERVICE_ENABLED these source the
// people list + add-child from the CENTRAL accounts people API; flag OFF is the
// legacy family_members path byte-for-byte (getStorySubjects → getMembers).
import { getStorySubjects, addStorySubject } from '@/contexts/family/actions/storySubjects';
import type { ContentParameters } from './ParametersStep';
import { BasisPicker } from './BasisPicker';
import { BookSizeChoice } from './BookSizeChoice';
import { bookSizesFor, DEFAULT_BOOK_SIZE } from '@/contexts/stories/utils/storyShape';

/** Client age→wizard-ageGroup (mirrors PgFamilyAdapter.calculateAgeGroup but
 *  keeps the wizard's 5-bucket scale incl. '1-3'). */
function ageGroupFromBirthDate(birthDate?: string | null): string | null {
  if (!birthDate) return null;
  const b = new Date(birthDate);
  if (Number.isNaN(b.getTime())) return null;
  const now = new Date();
  let age = now.getFullYear() - b.getFullYear();
  const m = now.getMonth() - b.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < b.getDate())) age--;
  if (age < 3) return '1-3';
  if (age < 5) return '3-5';
  if (age < 7) return '5-7';
  if (age < 10) return '7-10';
  return '10+';
}

// Idea chips live in the admin (story_ideas); contexts/templates/seed/storyIdeasSeed.ts
// is the fallback.

type ChildOption = { id: string; name: string; birthDate?: string | null };

const GENRES = [
  'lullaby', 'adventure', 'comedy', 'fable',
  'mystery', 'everyday', 'educational', 'classic',
] as const;

const AGE_GROUPS = ['1-3', '3-5', '5-7', '7-10', '10+'] as const;
const LANGUAGES = ['ru', 'en', 'cs', 'uk', 'de', 'fr'] as const;

// From-text transform modes — how the engine story-gen step treats the paste.
const FROM_TEXT_MODES = ['verbatim', 'translate', 'recast_hero', 'based_on', 'incorporate'] as const;

const THEMES = [
  'friendship', 'courage', 'kindness',
  'responsibility', 'curiosity', 'acceptance',
] as const;

const SETTINGS = [
  'fairytale', 'nature', 'real_world', 'space', 'underwater',
] as const;

const GENRE_ENERGY_DEFAULTS: Record<string, number> = {
  lullaby: 1,
  bedtime: 1,
  fable: 2,
  educational: 2,
  everyday: 3,
  classic: 3,
  comedy: 4,
  adventure: 4,
  mystery: 3,
};

const ENERGY_OPTIONS = [
  { value: '1', key: '1' },
  { value: '2', key: '2' },
  { value: '3', key: '3' },
  { value: '4', key: '4' },
  { value: '5', key: '5' },
] as const;

interface StoryInputStepProps {
  values: ContentParameters;
  /**
   * Takes a whole value OR an updater, and an updater is what anything fired
   * from a CHILD must use. A handler that spreads the `values` it closed over
   * writes back the state as it was when that render happened: tick "the text
   * is mine", then pick the heroes to swap in, and the tick is gone — the form
   * sat on a disabled "Next" under a hint asking for exactly that tick. The
   * parent's setState resolves an updater against the LATEST state, so nothing
   * a later render changed can be undone by an earlier one.
   */
  onChange: (values: ContentParameters | ((prev: ContentParameters) => ContentParameters)) => void;
  locale: string;
  recentCharacterIds?: string[];
  onCharacterNamesChange?: (names: Record<string, string>) => void;
  /** True when a dedicated Heroes step precedes this one (the normal generate
   *  flow). Then the generate tab's own "про кого" picker is a duplicate and is
   *  hidden — heroes come from the Heroes step alone. Remix has NO heroes step,
   *  so it keeps the inline picker. */
  heroesInFlow?: boolean;
  /** From the "по мотивам" (basedon) outcome tile → the from-text step is locked
   *  to based_on: the verbatim/translate/recast mode selector is hidden. */
  basedOnOnly?: boolean;
  /** id→name of characters picked earlier (heroes step) — offered as one-tap
   *  "replace with" candidates for the basis cast. */
  characterNames?: Record<string, string>;
  /** The JOB's own words for this screen (see jobRoads / content.wizard.jobs). */
  title?: string;
  subtitle?: string;
}

/** Beyond this, a paste into the idea field is treated as a finished text. */
const LONG_PASTE_CHARS = 1200;

export function StoryInputStep({ values, onChange, locale, recentCharacterIds, onCharacterNamesChange, heroesInFlow, basedOnOnly, characterNames: pickedNames, title, subtitle }: StoryInputStepProps) {
  const t = useTranslations('content.wizard');
  // UI locale (not the `locale` prop, which may carry the story language) — drives
  // the localized idea seeds so chips match the interface language.
  const uiLocale = useLocale();
  // Genres come from the published story templates (admin → Templates); the
  // built-in list is the fallback until they load or when they are unavailable.
  const offered = useOfferedTemplates();
  const genreKeys: readonly string[] = offered?.stories.length ? offered.stories.map((s) => s.key) : GENRES;
  const genreLabel = (key: string) =>
    templateTitle(offered?.stories.find((s) => s.key === key)?.title, uiLocale) ?? t(`genres.${key}`);
  const genreEnergy = (key: string): number | undefined =>
    offered?.stories.find((s) => s.key === key)?.energy ?? GENRE_ENERGY_DEFAULTS[key];
  const energyManuallySet = useRef(false);

  // Updaters only, never a spread of `values`: a change made from a callback that runs later (a
  // child reporting back after its data loads) spread the render's OLD values and wiped what the
  // parent had just typed — the prompt went missing on the review step (e2e, 1 run in 5).
  const update = (field: keyof ContentParameters, value: string | number | undefined) => {
    onChange((prev) => ({ ...prev, [field]: value }));
  };

  // "Для кого" — children from the family profile (audience picker).
  const [children, setChildren] = useState<ChildOption[]>([]);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  // Names of picked story characters (для «про кого») — used to personalise ideas.
  const [charNames, setCharNames] = useState<Record<string, string>>({});
  const handleCharacterNames = (names: Record<string, string>) => {
    setCharNames(names);
    onCharacterNamesChange?.(names);
  };
  const [addingChild, setAddingChild] = useState(false);
  const [newChildName, setNewChildName] = useState('');
  const [newChildBirth, setNewChildBirth] = useState('');
  const [savingChild, setSavingChild] = useState(false);

  // From-text hero-mode character pickers. The persisted values only carry
  // names (fromTextReplacements[].to / fromTextInjectHeroes), so the picked ids
  // + their id→name maps live in local state to drive CharacterSelector.
  const [recastIds, setRecastIds] = useState<string[]>([]);
  const [recastNames, setRecastNames] = useState<Record<string, string>>({});
  const [injectIds, setInjectIds] = useState<string[]>([]);
  const [injectNames, setInjectNames] = useState<Record<string, string>>({});
  const [basedOnIds, setBasedOnIds] = useState<string[]>([]);
  const [basedOnNames, setBasedOnNames] = useState<Record<string, string>>({});

  // Pasted-text language: detected from the text itself (script + function
  // words), applied until the user picks a language by hand. Before, from-text
  // always used the UI locale — a Russian paste in an English UI got English
  // transform prompts and an English voice.
  const languageTouched = !!values.languageTouched;
  /** A paste this long is a story, not an idea — the step asks instead of cutting. */
  const [longPaste, setLongPaste] = useState<string | null>(null);
  const [detectedLanguage, setDetectedLanguage] = useState<string | null>(null);
  const pastedText = values.text ?? '';
  useEffect(() => {
    if (values.creationMode !== 'from-text' || values.basisStoryId) return;
    const detected = detectLanguage(pastedText);
    setDetectedLanguage(detected);
    if (detected && !languageTouched && detected !== values.language) {
      onChange((prev) => ({ ...prev, language: detected }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pastedText, values.creationMode, values.basisStoryId]);

  // based_on: EXPLICIT intent. Legacy drafts without it: a change vector means
  // "reimagine"; plain hero swaps mean "retell".
  const basedOnMode: 'retell' | 'reimagine' =
    values.fromTextBasedOnMode ??
    (values.fromTextChangeVector?.trim() ? 'reimagine' : (values.fromTextReplacements?.length ? 'retell' : 'reimagine'));
  // Cast of the picked admin basis text (name+description). When present, the
  // user can rename each hero → fromTextReplacements (keep story, swap heroes).
  const [basisCast, setBasisCast] = useState<{ name: string; description: string }[]>([]);
  // Which library character (by ID) is the picked replacement for each basis
  // slot — so two characters that share a NAME (e.g. two "Ника") don't both
  // highlight. The replacement pair itself is still name-based (what the worker
  // needs); this only disambiguates the active chip.
  const [basisReplCharByFrom, setBasisReplCharByFrom] = useState<Record<string, string>>({});

  // Photo-hero add IN the replacement flow: a curator can add themself / other
  // family members as photo-heroes right here (not only in HeroesStep), then pick
  // them as a cast replacement. Same emit-only draft as HeroesStep.onPhotoFile.
  // Created AT UPLOAD (pipeline v2): the record + its identity job exist at once.
  const photoHeroes = values.photoHeroes ?? [];
  const [photoBusy, setPhotoBusy] = useState(false);
  const [photoErr, setPhotoErr] = useState<string | null>(null);
  const [photoConsent, setPhotoConsent] = useState(false);
  const addPhotoHero = (file: File | undefined) => {
    setPhotoErr(null);
    if (!file || photoBusy) return;
    if (!photoConsent) { setPhotoErr(t('heroes.fromPhotoConsentRequired')); return; }
    setPhotoBusy(true);
    // See HeroesStep: downscale first, or a phone photograph overruns the
    // Server Action body limit once base64 has grown it by a third.
    void (async () => {
      try {
        const b64 = await fileToBase64(file);
        const res = await createPhotoHero({ imageBase64: b64, consentVersion: PHOTO_CONSENT_VERSION, language: values.language });
        if (!res.success) { setPhotoErr(res.error); return; }
        onChange((prev) => withPhotoHero(prev, {
          characterId: res.data.characterId, name: res.data.name, photoKey: res.data.photoKey, previewUrl: res.data.previewUrl,
        }));
      } catch {
        setPhotoErr(t('heroes.fromPhotoReadError'));
      } finally {
        setPhotoBusy(false);
      }
    })();
  };
  const setPhotoHeroName = (characterId: string, name: string) =>
    onChange((prev) => withPhotoHeroName(prev, characterId, name));
  const commitPhotoHeroName = (characterId: string, name: string) => {
    if (name.trim()) void renamePhotoHero(characterId, name);
  };
  const removePhotoHero = (characterId: string) =>
    onChange((prev) => withoutPhotoHero(prev, characterId));

  useEffect(() => {
    let alive = true;
    getStorySubjects()
      .then((members) => {
        if (!alive || !members) return;
        setChildren(
          members
            .filter((m) => m.isChild)
            .map((m) => ({ id: m.id, name: m.name, birthDate: m.birthDate })),
        );
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  const saveNewChild = async () => {
    const name = newChildName.trim();
    if (!name || savingChild) return;
    setSavingChild(true);
    try {
      const res = await addStorySubject({ name, birthDate: newChildBirth || undefined });
      if (res.success && res.data) {
        const child: ChildOption = { id: res.data.id, name: res.data.name, birthDate: res.data.birthDate };
        setChildren((prev) => [...prev, child]);
        setAddingChild(false);
        setNewChildName('');
        setNewChildBirth('');
        selectChild(child);
      }
    } finally {
      setSavingChild(false);
    }
  };

  const selectChild = (child: ChildOption) => {
    const derivedAge = ageGroupFromBirthDate(child.birthDate);
    const picked = values.audienceChildId === child.id;
    onChange((prev) => ({
      ...prev,
      audienceChildId: picked ? undefined : child.id,
      audienceChildName: picked ? undefined : child.name,
      // Pick → auto-fill age from the child's birthDate (if known) — but NEVER
      // over an age the user already chose by hand (a "7 years" pick was being
      // silently replaced by a stale birth date → a 42-block toddler story).
      ...(!picked && derivedAge && !values.ageGroupTouched ? { ageGroup: derivedAge } : {}),
    }));
  };

  // Idea suggestions built around the story's HERO. Priority: a picked story
  // character (про кого) > the audience child (для кого) > a neutral hero word.
  // (Roles hero/antagonist are also mapped into the plot by the workflow's
  // outline step from characterAssignments, independent of the prompt.)
  const heroCharacterName =
    values.characterIds.map((id) => charNames[id]).find(Boolean);
  // The neutral fallback is localized too — a German visitor used to read
  // "the hero findet im Garten …" in the idea chips.
  const heroWord = heroCharacterName || values.audienceChildName || t('storyInput.heroWord');
  // Plot ideas (admin → Story ideas): the chosen genre's and the general ones, in
  // the interface language; the built-in list until they load.
  const adminIdeas = useWizardIdeas(uiLocale, values.genre);
  const ideaSource = adminIdeas?.length ? adminIdeas : (IDEA_SEEDS[uiLocale] || IDEA_SEEDS.en);
  const ideaSeeds = ideaSource.map((s) => s.replace('{name}', heroWord));
  const applyIdea = (idea: string) => update('prompt', idea);
  const surpriseMe = () => update('prompt', ideaSeeds[Math.floor(Math.random() * ideaSeeds.length)]);

  // ── From-text hero modes ──
  const fromTextMode = values.fromTextMode ?? 'verbatim';
  // based_on-locked: the "по мотивам" intent, OR the generate-flow fast-path
  // where the parent chose "start from a ready story" (creationMode flipped to
  // from-text). In both, we lock to based_on and hide the mode selector.
  const basedOnLocked = basedOnOnly || (heroesInFlow && values.creationMode === 'from-text');
  const startFromBasis = () =>
    // The "what to change" vector is independent — never seeded from the generate
    // prompt (that leaked stale prompts from a prior attempt into this field).
    onChange((prev) => ({ ...prev, creationMode: 'from-text', fromTextMode: 'based_on' }));

  // recast_hero: the picked characters become the story's heroes; whom each
  // replaces is picked by entity id in ReplacePicker (pipeline v2, node 'pick').
  const rebuildRecast = (ids: string[], _names: Record<string, string>) => {
    onChange((prev) => ({
      ...prev,
      // Register the picked characters as the story's characters, so the
      // illustrations reuse THEIR appearance (not a freshly invented look).
      characterIds: ids,
      // a pick of a hero no longer chosen is dropped
      replaceMap: (prev.replaceMap ?? []).filter((m) => ids.includes(m.characterId)),
    }));
  };

  // incorporate: picked character names woven into the source story.
  const rebuildInject = (ids: string[], names: Record<string, string>) => {
    onChange((prev) => ({
      ...prev,
      fromTextInjectHeroes: ids.map((id) => names[id]).filter(Boolean) as string[],
      characterIds: ids, // link picked characters → their appearance in illustrations
    }));
  };

  // based_on: the picked character stars in the new inspired-by story. One hero
  // → fromTextHeroName (the engine interpolates it).
  const rebuildBasedOn = (ids: string[], names: Record<string, string>) => {
    const name = ids.map((id) => names[id]).find(Boolean);
    onChange((prev) => ({ ...prev, fromTextHeroName: name || undefined, characterIds: ids }));
  };

  // Basis cast-swap: the picked basis lists its own heroes; the user renames the
  // ones they want to change → fromTextReplacements ({ from: basis name, to:
  // typed name }). Left blank = keep that hero as-is.
  const basisReplByFrom: Record<string, string> = {};
  for (const r of values.fromTextReplacements ?? []) if (r.from) basisReplByFrom[r.from] = r.to;
  const buildPairs = (map: Record<string, string>) =>
    basisCast.map((c) => ({ from: c.name, to: (map[c.name] ?? '').trim() })).filter((p) => p.to);
  const setBasisReplacement = (fromName: string, to: string) => {
    if (!to) {
      // Clearing this slot → drop its remembered character id too.
      setBasisReplCharByFrom((prev) => {
        const next = { ...prev };
        delete next[fromName];
        return next;
      });
    }
    onChange((prev) => ({
      ...prev,
      fromTextReplacements: buildPairs({ ...basisReplByFrom, [fromName]: to }).length
        ? buildPairs({ ...basisReplByFrom, [fromName]: to })
        : undefined,
    }));
  };
  // Pick one of YOUR library characters as the replacement — carries the name AND
  // adds the character id so the story uses THAT character's appearance.
  const setBasisReplacementChar = (fromName: string, char: { id: string; name: string }) => {
    const pairs = buildPairs({ ...basisReplByFrom, [fromName]: char.name });
    const ids = new Set(values.characterIds ?? []);
    ids.add(char.id);
    setBasisReplCharByFrom((prev) => ({ ...prev, [fromName]: char.id }));
    onChange((prev) => ({
      ...prev,
      fromTextReplacements: pairs.length ? pairs : undefined,
      characterIds: [...ids],
    }));
  };
  // Only the cast (local state) — the basisStoryId + replacements reset happen in
  // ONE onChange in the JSX to avoid clobbering values with a stale snapshot.
  const onPickBasis = (item: { characters: { name: string; description: string }[] } | undefined) => {
    setBasisCast(item?.characters ?? []);
  };

  // Your library characters — offered as one-tap replacements for the basis cast.
  const { user: currentUser } = useCurrentUser();
  const { data: libraryChars = [] } = useCharacters(currentUser?.familyId ?? undefined);
  const castSelectorRef = useRef<CharacterSelectorHandle>(null);

  return (
    <div data-testid="content-wizard-story-input-step" className="space-y-3 sm:space-y-4">
      {/* The JOB's own question, when there is one. "Описание" over a text area
          is a form label; "Про что сказка?" is what the parent came to answer. */}
      {(title || subtitle) && (
        <div className="space-y-1">
          {title && <h3 className="text-base font-medium">{title}</h3>}
          {subtitle && <p className="text-sm text-muted-foreground">{subtitle}</p>}
        </div>
      )}
      {/* The input METHOD is chosen upfront on the inputMode step, so there is no
          in-form generate↔from-text switcher — we render only the form for the
          already-chosen creationMode. (Tabs kept purely as the conditional
          container; the tab list/switcher is gone.) */}
      <Tabs value={values.creationMode} data-testid="story-input-tabs">
        {/* ── Generate form ── */}
        <TabsContent value="generate" className="space-y-3 sm:space-y-4">
          {/* HERO: the idea prompt comes FIRST — the parent writes their idea
              immediately instead of facing a wall of dropdowns. */}
          <Card className="border-primary">
            <CardContent className="pt-4 pb-3 px-3 sm:px-6 space-y-2">
              {/* "Описание" under "Про что сказка?" is the same question twice. */}
              {!title && <Label htmlFor="content-prompt">{t('parameters.prompt')}</Label>}
              <Textarea
                id="content-prompt"
                data-testid="content-prompt-input"
                placeholder={t('parameters.promptPlaceholder')}
                value={values.prompt}
                onChange={(e) => update('prompt', e.target.value)}
                onPaste={(e) => {
                  // A long paste is almost never an idea, so we offer the field
                  // that was built for a text (50 000). But the paste itself is
                  // only HELD BACK when it would not fit: swallowing text that
                  // fits leaves the parent staring at an empty field and a
                  // counter saying 0, which is what it did.
                  const pasted = e.clipboardData.getData('text').trim();
                  if (pasted.length < LONG_PASTE_CHARS) return;
                  const fits = (values.prompt.length + pasted.length) <= 5000;
                  if (!fits) e.preventDefault();
                  setLongPaste(pasted);
                }}
                rows={5}
                maxLength={5000}
                className="resize-none min-h-[110px] sm:min-h-[130px]"
              />

              {/* Asked, never decided silently: an idea and a finished story are
                  two different creations, and only the parent knows which this is. */}
              {longPaste && (
                <div
                  className="space-y-2 rounded-xl border border-primary/40 bg-primary/5 p-3"
                  data-testid="long-paste-offer"
                >
                  <p className="text-sm font-medium">
                    {t('parameters.longPaste.title', { count: longPaste.length })}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {/* Only say "it would be cut" when it actually would: the idea
                        field holds 5000, and most pasted stories are shorter. The
                        reason to offer is that an IDEA and a TEXT are treated
                        differently, not that something is about to be lost. */}
                    {longPaste.length > 5000
                      ? t('parameters.longPaste.hintLong')
                      : t('parameters.longPaste.hint')}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      type="button"
                      size="sm"
                      data-testid="long-paste-use-as-text"
                      onClick={() => {
                        onChange((prev) => ({
                          ...prev,
                          creationMode: 'from-text',
                          fromTextMode: 'verbatim',
                          text: longPaste,
                          // Back returns to the idea form, not to the previous
                          // step: this is where the parent came from.
                          fromTextFromIdea: true,
                        }));
                        setLongPaste(null);
                      }}
                    >
                      {t('parameters.longPaste.asText')}
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      data-testid="long-paste-as-idea"
                      onClick={() => {
                        // Already in the field when it fitted; only a held-back
                        // paste still has to be put there (trimmed).
                        if (!values.prompt.includes(longPaste.slice(0, 200))) {
                          update('prompt', `${values.prompt}${longPaste}`.slice(0, 5000));
                        }
                        setLongPaste(null);
                      }}
                    >
                      {longPaste.length > 5000
                        ? t('parameters.longPaste.asIdeaTrim')
                        : t('parameters.longPaste.asIdea')}
                    </Button>
                  </div>
                </div>
              )}
              {/* Anti-blank-page: tap an idea (personalised with the child's
                  name) or roll a random one. */}
              <div className="flex flex-wrap gap-1.5" data-testid="idea-chips">
                {ideaSeeds.slice(0, 4).map((idea, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => applyIdea(idea)}
                    className="text-xs rounded-full border px-2.5 py-1 text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors"
                    data-testid={`idea-chip-${i}`}
                  >
                    {idea}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={surpriseMe}
                  className="text-xs rounded-full border border-primary/40 px-2.5 py-1 text-primary hover:bg-primary/10 transition-colors inline-flex items-center gap-1"
                  data-testid="idea-surprise"
                >
                  <Dices className="h-3.5 w-3.5" />
                  {t('storyInput.surprise')}
                </button>
                {/* Voice → prompt: record an idea, drop the transcript into the
                    field (editable; screened at generation like any prompt). */}
                <VoiceToPromptButton
                  onText={(text) =>
                    update('prompt', values.prompt ? `${values.prompt} ${text}`.trim() : text)
                  }
                />
              </div>
              <p className={cn(
                'text-xs',
                values.prompt.length > 0 && values.prompt.length < 10
                  ? 'text-warning-foreground dark:text-warning'
                  : 'text-muted-foreground',
              )}>
                {t('parameters.promptHint', { count: values.prompt.length })}
                {values.prompt.length > 0 && values.prompt.length < 10 && (
                  <span data-testid="content-prompt-min-hint"> — {t('parameters.promptMinHint')}</span>
                )}
              </p>
            </CardContent>
          </Card>

          {/* Fast path: start from a ready public story instead of writing an idea.
              Prominent (equal footing with writing) — flips to based_on in the same
              step, where the parent swaps heroes. */}
          {heroesInFlow && (
            <>
              <div className="flex items-center gap-3">
                <div className="h-px flex-1 bg-border" />
                <span className="text-xs uppercase tracking-wide text-muted-foreground">{t('storyInput.or')}</span>
                <div className="h-px flex-1 bg-border" />
              </div>
              <button
                type="button"
                onClick={startFromBasis}
                className="flex w-full items-center gap-3 rounded-xl border-2 border-primary/30 bg-primary/5 px-4 py-3 text-left transition-colors hover:border-primary/60"
                data-testid="content-start-from-basis"
              >
                <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                  <Sparkles className="h-5 w-5" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium">{t('storyInput.startFromBasis')}</p>
                  <p className="text-xs text-muted-foreground">{t('storyInput.startFromBasisHint')}</p>
                </div>
              </button>
            </>
          )}

          {/* First-screen essentials: "для кого" (child OR manual age) + genre. */}
          <div className="grid gap-2 sm:gap-4 grid-cols-1 sm:grid-cols-2">
            {/* Для кого — pick a child (auto age) OR set the age manually. */}
            <div className="space-y-1.5">
              <Label className="text-xs sm:text-sm">{t('storyInput.forWho')}</Label>
              <div className="flex flex-wrap gap-1.5" data-testid="audience-children">
                {children.map((child) => (
                  <button
                    key={child.id}
                    type="button"
                    onClick={() => selectChild(child)}
                    className={cn(
                      'text-xs rounded-full border px-3 py-1.5 transition-colors',
                      values.audienceChildId === child.id
                        ? 'border-primary bg-primary/10 text-primary'
                        : 'text-muted-foreground hover:bg-accent',
                    )}
                    data-testid={`audience-child-${child.id}`}
                  >
                    {child.name}
                  </button>
                ))}
                {!addingChild && (
                  <button
                    type="button"
                    onClick={() => setAddingChild(true)}
                    className="text-xs rounded-full border border-dashed px-3 py-1.5 text-muted-foreground hover:bg-accent transition-colors"
                    data-testid="audience-add-child"
                  >
                    + {t('storyInput.addChild')}
                  </button>
                )}
              </div>
              {addingChild && (
                <div className="flex flex-wrap items-center gap-2 pt-1" data-testid="audience-add-child-form">
                  <Input
                    autoFocus
                    value={newChildName}
                    onChange={(e) => setNewChildName(e.target.value)}
                    placeholder={t('storyInput.childNamePlaceholder')}
                    maxLength={60}
                    className="h-9 w-32 text-xs"
                    data-testid="audience-child-name-input"
                    onKeyDown={(e) => { if (e.key === 'Enter') saveNewChild(); }}
                  />
                  <Input
                    type="date"
                    value={newChildBirth}
                    onChange={(e) => setNewChildBirth(e.target.value)}
                    className="h-9 w-36 text-xs"
                    data-testid="audience-child-birth-input"
                  />
                  <Button
                    type="button"
                    size="sm"
                    className="h-9"
                    disabled={!newChildName.trim() || savingChild}
                    onClick={saveNewChild}
                    data-testid="audience-child-save"
                  >
                    {t('storyInput.addChildSave')}
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    className="h-9"
                    onClick={() => { setAddingChild(false); setNewChildName(''); setNewChildBirth(''); }}
                  >
                    ✕
                  </Button>
                </div>
              )}
              {/* Either/or: a picked child derives the age; otherwise set it manually. */}
              {values.audienceChildId ? (
                <p className="text-xs text-muted-foreground pt-0.5" data-testid="audience-derived-age">
                  {t('parameters.ageGroup')}: {t(`ageGroups.${values.ageGroup}`)}
                  <button
                    type="button"
                    className="ml-2 underline hover:text-foreground"
                    onClick={() => onChange((prev) => ({ ...prev, audienceChildId: undefined, audienceChildName: undefined }))}
                    data-testid="audience-clear-child"
                  >
                    {t('storyInput.setAgeManually')}
                  </button>
                </p>
              ) : (
                <Select value={values.ageGroup} onValueChange={(v) => onChange((prev) => ({ ...prev, ageGroup: v, ageGroupTouched: true }))}>
                  <SelectTrigger className="w-full text-xs sm:text-sm h-10 px-2 sm:px-3" data-testid="content-age-group-select">
                    <SelectValue placeholder={t('parameters.ageGroup')} />
                  </SelectTrigger>
                  <SelectContent>
                    {AGE_GROUPS.map((ag) => (
                      <SelectItem key={ag} value={ag}>{t(`ageGroups.${ag}`)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
            {/* Genre — kept on the first screen (materially shapes tone). */}
            <div className="space-y-1.5">
              <Label className="text-xs sm:text-sm">{t('parameters.genre')}</Label>
              <Select value={values.genre} onValueChange={(v) => {
                const updates: Partial<ContentParameters> = { genre: v };
                const energy = genreEnergy(v);
                if (!energyManuallySet.current && energy !== undefined) {
                  updates.energy = energy;
                }
                onChange((prev) => ({ ...prev, ...updates }));
              }}>
                <SelectTrigger className="w-full text-xs sm:text-sm h-10 px-2 sm:px-3" data-testid="content-genre-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {genreKeys.map((genre) => (
                    <SelectItem key={genre} value={genre}>{genreLabel(genre)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Story language — visible on the first screen (defaults to the interface
                language). Not buried in Advanced, so it can be changed at a glance. */}
            <div className="space-y-1.5">
              <Label className="text-xs sm:text-sm">{t('parameters.language')}</Label>
              <Select value={values.language} onValueChange={(v) => update('language', v)}>
                <SelectTrigger className="w-full text-xs sm:text-sm h-10 px-2 sm:px-3" data-testid="content-language-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {LANGUAGES.map((lang) => (
                    <SelectItem key={lang} value={lang}>{t(`languages.${lang}`)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* "Про кого" — hero characters (optional). Hidden when a dedicated
              Heroes step already picked them (normal generate flow); shown for
              remix, which has no Heroes step. */}
          {!heroesInFlow && (
            <Card>
              <CardContent className="pt-4 pb-3 px-3 sm:px-6">
                <p className="text-sm font-medium mb-2">{t('characters.title')}</p>
                <CharacterSelector
                  selectedCharacterIds={values.characterIds}
                  onSelectionChange={(ids) => onChange((prev) => ({ ...prev, characterIds: ids }))}
                  onAssignmentsChange={(assignments) => onChange((prev) => ({ ...prev, characterAssignments: assignments }))}
                  onNamesChange={handleCharacterNames}
                  locale={locale}
                  recentCharacterIds={recentCharacterIds}
                />
                {values.characterIds.length === 0 && (
                  <p className="text-xs text-muted-foreground mt-1">{t('characters.none')}</p>
                )}
              </CardContent>
            </Card>
          )}

          {/* Advanced — everything with a sensible default is tucked away so the
              first screen stays about idea + who. */}
          <Collapsible open={advancedOpen} onOpenChange={setAdvancedOpen}>
            <CollapsibleTrigger asChild>
              <button
                type="button"
                className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
                data-testid="content-advanced-toggle"
              >
                <ChevronDown className={cn('h-4 w-4 transition-transform', advancedOpen && 'rotate-180')} />
                {t('parameters.advancedSettings')}
              </button>
            </CollapsibleTrigger>
            <CollapsibleContent className="space-y-3 sm:space-y-4 pt-3">
              {/* Theme | Setting */}
              <div className="grid gap-2 sm:gap-4 grid-cols-1 sm:grid-cols-2">
                <div className="space-y-1">
                  <Label className="text-xs sm:text-sm">{t('parameters.theme')}</Label>
                  <Select value={values.theme ?? ''} onValueChange={(v) => update('theme', v || undefined)}>
                    <SelectTrigger className="w-full text-xs sm:text-sm h-10 px-2 sm:px-3" data-testid="content-theme-select">
                      <SelectValue placeholder={t('parameters.themePlaceholder')} />
                    </SelectTrigger>
                    <SelectContent>
                      {THEMES.map((theme) => (
                        <SelectItem key={theme} value={theme}>{t(`themes.${theme}`)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs sm:text-sm">{t('parameters.setting')}</Label>
                  <Select value={values.setting ?? ''} onValueChange={(v) => update('setting', v || undefined)}>
                    <SelectTrigger className="w-full text-xs sm:text-sm h-10 px-2 sm:px-3" data-testid="content-setting-select">
                      <SelectValue placeholder={t('parameters.settingPlaceholder')} />
                    </SelectTrigger>
                    <SelectContent>
                      {SETTINGS.map((s) => (
                        <SelectItem key={s} value={s}>{t(`settings.${s}`)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Duration | Energy */}
              <div className="grid gap-2 sm:gap-4 grid-cols-1 sm:grid-cols-2">
                <BookSizeChoice
                  compact
                  pages={values.pageCount ?? bookSizesFor(values.ageGroup)[DEFAULT_BOOK_SIZE]}
                  ageGroup={values.ageGroup}
                  language={values.language}
                  onChange={(pages) => update('pageCount', pages)}
                />
                <div className="space-y-1">
                  <Label className="text-xs sm:text-sm">{t('parameters.energy')}</Label>
                  <Select
                    value={String(values.energy ?? 3)}
                    onValueChange={(v) => {
                      energyManuallySet.current = true;
                      update('energy', Number(v));
                    }}
                  >
                    <SelectTrigger className="w-full text-xs sm:text-sm h-10 px-2 sm:px-3" data-testid="content-energy-select">
                      <SelectValue placeholder={t('parameters.energyPlaceholder')} />
                    </SelectTrigger>
                    <SelectContent>
                      {ENERGY_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {t(`energyLevels.${opt.key}`)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CollapsibleContent>
          </Collapsible>
        </TabsContent>

        {/* ── From-text tab ── */}
        <TabsContent value="from-text" className="space-y-3 sm:space-y-4">
          {/* No second way back: the wizard's own Back button returns to the
              idea form from here (index.tsx handleBack), and two buttons that
              both mean "back" disagreeing with each other is worse than one. */}

          {/* Title */}
          <div className="space-y-2">
            <Label htmlFor="content-title">{t('parameters.title')}</Label>
            <Input
              id="content-title"
              data-testid="content-title-input"
              placeholder={t('parameters.titlePlaceholder')}
              value={values.title ?? ''}
              onChange={(e) => update('title', e.target.value)}
              maxLength={100}
            />
            <p className="text-xs text-muted-foreground">
              {t('parameters.titleHint', { count: values.title?.length ?? 0 })}
            </p>
          </div>

          {/* Text — hidden ONLY when a ready story supplies it (a picked basis).
              It used to be hidden for the whole based_on-locked fast-path, which
              includes a parent who came through "heroes first" with a text of
              their own: they saw the copyright checkbox, pasted into nothing, and
              the counter stayed at 0. */}
          {!values.basisStoryId && (
            <div className="space-y-2">
              <Label htmlFor="content-text">{t('parameters.text')}</Label>
              <Textarea
                id="content-text"
                data-testid="content-text-input"
                placeholder={t('parameters.textPlaceholder')}
                value={values.text ?? ''}
                onChange={(e) => update('text', e.target.value)}
                rows={10}
                maxLength={50000}
                className="min-h-[200px] resize-y"
              />
              <p className={cn(
                'text-xs',
                (values.text?.length ?? 0) > 0 && (values.text?.length ?? 0) < 100
                  ? 'text-warning-foreground dark:text-warning'
                  : 'text-muted-foreground',
              )}>
                {t('parameters.textHint', { count: values.text?.length ?? 0 })}
                {(values.text?.length ?? 0) > 0 && (values.text?.length ?? 0) < 100 && (
                  <span> — {t('parameters.textMinHint')}</span>
                )}
              </p>
              {detectedLanguage && (
                <p className="text-xs text-muted-foreground" data-testid="content-text-language-detected">
                  {t('storyInput.languageDetected', { language: t(`languages.${detectedLanguage}`) })}
                </p>
              )}
            </div>
          )}

          {/* Mode — hidden when locked to based_on (fast-path / "по мотивам" tile). */}
          {!basedOnLocked && (
            <div className="space-y-1">
              <Label className="text-xs sm:text-sm">{t('storyInput.fromTextMode')}</Label>
              <Select
                value={values.fromTextMode ?? 'verbatim'}
                onValueChange={(v) => {
                  // Switching mode resets the picked-character link so the previous
                  // mode's characterIds don't leak into the new mode.
                  setRecastIds([]); setInjectIds([]); setBasedOnIds([]);
                  onChange((prev) => ({
                    ...prev,
                    fromTextMode: v as ContentParameters['fromTextMode'],
                    characterIds: [],
                    fromTextReplacements: undefined,
                    fromTextInjectHeroes: undefined,
                  }));
                }}
              >
                <SelectTrigger className="w-full text-xs sm:text-sm h-10 px-2 sm:px-3" data-testid="content-fromtext-mode-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FROM_TEXT_MODES.map((m) => (
                    <SelectItem key={m} value={m} data-testid={`content-fromtext-mode-option-${m}`}>
                      {t(`storyInput.fromTextModes.${m}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {/* Explain what the chosen mode does to the pasted text. */}
              <p className="text-xs text-muted-foreground" data-testid="content-fromtext-mode-hint">
                {t(`storyInput.fromTextModeHints.${values.fromTextMode ?? 'verbatim'}`)}
              </p>
            </div>
          )}

          {/* recast_hero — pick your hero(es); they take the lead of the SOURCE
              story (their face is used in the illustrations too). Naming which
              character each replaces is OPTIONAL — leave it blank to just make
              your character the main hero. The engine keeps the source language
              and fixes gendered agreement automatically. */}
          {fromTextMode === 'recast_hero' && (
            <div className="space-y-2">
              <Label className="text-xs sm:text-sm">{t('storyInput.fromTextPickCharacters')}</Label>
              <CharacterSelector
                selectedCharacterIds={recastIds}
                onSelectionChange={(ids) => { setRecastIds(ids); rebuildRecast(ids, recastNames); }}
                onNamesChange={(names) => { setRecastNames(names); rebuildRecast(recastIds, names); }}
                locale={locale}
                recentCharacterIds={recentCharacterIds}
              />
              {/* v2: parse the text here, before the order, and pick whom each hero replaces */}
              {recastIds.length > 0 && (
                <ReplacePicker
                  text={values.text}
                  language={values.language}
                  ageGroup={values.ageGroup}
                  title={values.title}
                  heroes={recastIds.map((id) => ({ characterId: id, name: recastNames[id] ?? id.slice(0, 8) }))}
                  value={values.replaceMap ?? []}
                  onChange={({ parsed, map }) => onChange((prev) => ({ ...prev, preParsed: parsed ?? undefined, replaceMap: map }))}
                />
              )}
            </div>
          )}

          {/* based_on — the new story is INSPIRED BY the source and STARS a
              character picked from the collection, optionally steered by a
              free-text change vector. */}
          {fromTextMode === 'based_on' && (
            <div className="space-y-3">
              {/* Public-gallery basis picker — seed from an Owlcat story instead
                  of pasted text (public-publishing-plan.md, feature 4). */}
              <BasisPicker
                value={values.basisStoryId}
                language={values.language}
                ageGroup={values.ageGroup}
                onChange={(id, title) =>
                  onChange((prev) => ({
                    ...prev,
                    basisStoryId: id,
                    fromTextReplacements: undefined,
                    // Seed the title from the basis in the SAME update (only if the
                    // user hasn't typed one) — a separate write clobbered basisStoryId.
                    ...(id && title && !values.title ? { title } : {}),
                  }))
                }
                onPick={onPickBasis}
              />

              {/* Two different actions used to hide behind one "по мотивам"
                  label: retell the SAME events with my heroes vs a NEW story
                  inspired by the source. Make the parent choose. */}
              <div className="space-y-1" data-testid="content-basedon-mode">
                <Label className="text-xs sm:text-sm">{t('storyInput.basedOnMode')}</Label>
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    type="button"
                    size="sm"
                    // The labels are full sentences in every language; on a phone
                    // they were clipped mid-word inside a fixed-height button.
                    className="h-auto min-h-9 whitespace-normal px-2 py-1.5 text-xs leading-tight sm:text-sm"
                    variant={basedOnMode === 'retell' ? 'default' : 'outline'}
                    onClick={() => onChange((prev) => ({ ...prev, fromTextBasedOnMode: 'retell', fromTextChangeVector: undefined }))}
                    data-testid="content-basedon-retell"
                  >
                    {t('storyInput.basedOnModeRetell')}
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    className="h-auto min-h-9 whitespace-normal px-2 py-1.5 text-xs leading-tight sm:text-sm"
                    variant={basedOnMode === 'reimagine' ? 'default' : 'outline'}
                    onClick={() => update('fromTextBasedOnMode', 'reimagine')}
                    data-testid="content-basedon-reimagine"
                  >
                    {t('storyInput.basedOnModeReimagine')}
                  </Button>
                </div>
                <p className="text-[11px] text-muted-foreground">
                  {basedOnMode === 'retell' ? t('storyInput.basedOnModeRetellHint') : t('storyInput.basedOnModeReimagineHint')}
                </p>
              </div>
              {basisCast.length > 0 ? (
                /* Basis with a known cast → let the user rename each hero (or keep
                    them). Populates fromTextReplacements. */
                <div className="space-y-2" data-testid="content-fromtext-basis-cast">
                  <Label className="text-xs sm:text-sm">{t('storyInput.basisCastLabel')}</Label>
                  <p className="text-[11px] text-muted-foreground">{t('storyInput.basisCastHint')}</p>
                  {/* v2: the classic is parsed (once, stored) and the picks are by entity id */}
                  {values.basisStoryId && (values.characterIds ?? []).length > 0 && (
                    <ReplacePicker
                      basisStoryId={values.basisStoryId}
                      language={values.language}
                      ageGroup={values.ageGroup}
                      title={values.title}
                      heroes={(values.characterIds ?? []).map((id) => ({
                        characterId: id,
                        name: libraryChars.find((lc) => lc.id === id)?.name
                          ?? photoHeroes.find((h) => h.characterId === id)?.name
                          ?? id.slice(0, 8),
                      }))}
                      value={values.replaceMap ?? []}
                      onChange={({ map }) => onChange((prev) => ({ ...prev, preParsed: undefined, replaceMap: map }))}
                    />
                  )}
                  <div className="space-y-1.5">
                    {basisCast.map((c, ci) => (
                      /* Row index is the stable handle: the character NAME is the
                         tale's own word and differs per language. */
                      <div key={c.name} className="space-y-1" data-testid={`content-fromtext-basis-cast-row-${ci}`}>
                        <div className="min-w-0">
                          <p className="truncate text-xs font-medium sm:text-sm">
                            {c.name}
                            {basisReplByFrom[c.name] && (
                              <span className="text-primary"> → {basisReplByFrom[c.name]}</span>
                            )}
                          </p>
                          {c.description && (
                            <p className="truncate text-[10px] text-muted-foreground">{c.description}</p>
                          )}
                        </div>
                        {/* Replace by PICKING one of your characters (or a just-added
                            photo hero, or create a new one). Tap the active one again
                            to keep the original. No free-text — pick only. */}
                        <div
                          className="flex flex-wrap items-center gap-1 pl-0.5"
                          data-testid={`content-fromtext-basis-cast-${c.name}`}
                          data-cast-index={ci}
                        >
                          {photoHeroes
                            .filter((h) => h.name?.trim() && !libraryChars.some((lc) => lc.id === h.characterId))
                            .map((h) => {
                              const active = basisReplCharByFrom[c.name] === h.characterId;
                              return (
                                <button
                                  key={`ph_${h.characterId}`}
                                  type="button"
                                  data-testid={`content-fromtext-basis-cast-photo-${ci}-${h.characterId}`}
                                  onClick={() => (active
                                    ? setBasisReplacement(c.name, '')
                                    : setBasisReplacementChar(c.name, { id: h.characterId, name: h.name.trim() }))}
                                  className={cn(
                                    'rounded-full border px-2 py-0.5 text-[10px] transition-colors',
                                    active ? 'border-primary bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-accent',
                                  )}
                                >
                                  {h.name.trim()}
                                </button>
                              );
                            })}
                          {libraryChars.map((lc) => {
                            // Active strictly by ID so two same-named characters
                            // (e.g. two "Ника") never both highlight. The chosen
                            // replacement name still shows inline ("Дед → Ника").
                            const active = basisReplCharByFrom[c.name] === lc.id;
                            return (
                              <button
                                key={lc.id}
                                type="button"
                                data-testid={`content-fromtext-basis-cast-pick-${ci}-${lc.id}`}
                                onClick={() => (active ? setBasisReplacement(c.name, '') : setBasisReplacementChar(c.name, lc))}
                                className={cn(
                                  'rounded-full border px-2 py-0.5 text-[10px] transition-colors',
                                  active ? 'border-primary bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-accent',
                                )}
                              >
                                {lc.name}
                              </button>
                            );
                          })}
                          <button
                            type="button"
                            data-testid={`content-fromtext-basis-cast-create-${ci}`}
                            onClick={() => castSelectorRef.current?.openCreate()}
                            className="inline-flex items-center gap-0.5 rounded-full border border-dashed px-2 py-0.5 text-[10px] text-muted-foreground hover:border-primary/50 hover:text-primary"
                          >
                            + {t('storyInput.basisCastCreate')}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                  {/* Add your OWN photo-heroes (self, family) right here, then pick
                      one above as a replacement — the same emit-only draft as the
                      heroes step, so the replacement flow is no longer read-only. */}
                  <div className="space-y-1.5 rounded-md border border-dashed p-2" data-testid="content-fromtext-photohero-add">
                    <p className="text-[11px] text-muted-foreground">
                      {t('storyInput.photoHeroAddHint')}
                    </p>
                    {photoHeroes.map((h) => (
                      <div key={h.characterId} className="flex items-center gap-2">
                        {h.previewUrl && (
                          // eslint-disable-next-line @next/next/no-img-element
                          <img src={h.previewUrl} alt="" className="h-8 w-8 shrink-0 rounded object-cover" />
                        )}
                        <input
                          value={h.name}
                          onChange={(e) => setPhotoHeroName(h.characterId, e.target.value)}
                          onBlur={(e) => commitPhotoHeroName(h.characterId, e.target.value)}
                          placeholder={t('storyInput.photoHeroNamePlaceholder')}
                          className={cn(
                            'min-w-0 flex-1 rounded border bg-background px-2 py-1 text-xs',
                            !h.name.trim() && 'border-destructive',
                          )}
                          data-testid={`content-fromtext-photohero-name-${h.characterId}`}
                        />
                        <button
                          type="button"
                          onClick={() => removePhotoHero(h.characterId)}
                          className="shrink-0 text-[11px] text-muted-foreground hover:text-destructive"
                          aria-label={t('storyInput.photoHeroRemove')}
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                    <PhotoConsentCheckbox
                      compact
                      checked={photoConsent}
                      onChange={(v) => { setPhotoConsent(v); setPhotoErr(null); }}
                    />
                    <label
                      className={cn(
                        'inline-flex items-center gap-0.5 rounded-full border border-dashed px-2 py-0.5 text-[10px]',
                        photoConsent
                          ? 'cursor-pointer text-muted-foreground hover:border-primary/50 hover:text-primary'
                          : 'cursor-not-allowed opacity-50 text-muted-foreground',
                      )}
                    >
                      {t('storyInput.photoHeroAdd')}
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        disabled={photoBusy || !photoConsent}
                        onChange={(e) => { addPhotoHero(e.target.files?.[0]); e.currentTarget.value = ''; }}
                        data-testid="content-fromtext-photohero-file"
                      />
                    </label>
                    {photoBusy && <span className="ml-2 text-[10px] text-muted-foreground">загрузка…</span>}
                    {photoErr && <p className="text-[10px] text-destructive">{photoErr}</p>}
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  <Label className="text-xs sm:text-sm">{t(`storyInput.fromTextHeroLabels.${fromTextMode}`)}</Label>
                  <div data-testid="content-fromtext-basedon-selector">
                    <CharacterSelector
                      selectedCharacterIds={basedOnIds}
                      onSelectionChange={(ids) => { setBasedOnIds(ids); rebuildBasedOn(ids, basedOnNames); }}
                      onNamesChange={(names) => { setBasedOnNames(names); rebuildBasedOn(basedOnIds, names); }}
                      locale={locale}
                      recentCharacterIds={recentCharacterIds}
                      maxCharacters={1}
                    />
                  </div>
                </div>
              )}
              {basedOnMode === 'reimagine' && (
              <div className="space-y-1">
                <Label htmlFor="content-fromtext-changevector" className="text-xs text-muted-foreground sm:text-sm">
                  {t('storyInput.fromTextChangeVector')} · {t('storyInput.optional')}
                </Label>
                <Textarea
                  id="content-fromtext-changevector"
                  data-testid="content-fromtext-changevector"
                  placeholder={t('storyInput.fromTextChangeVectorPlaceholder')}
                  value={values.fromTextChangeVector ?? ''}
                  onChange={(e) => update('fromTextChangeVector', e.target.value)}
                  rows={2}
                  maxLength={500}
                  className="resize-none text-xs sm:text-sm"
                />
                <p className="text-[11px] text-muted-foreground">{t('storyInput.fromTextChangeVectorEmptyHint')}</p>
              </div>
              )}
            </div>
          )}

          {/* incorporate — pick MULTIPLE characters to weave into the source
              story. The engine keeps the source language automatically. */}
          {fromTextMode === 'incorporate' && (
            <div className="space-y-2">
              <Label className="text-xs sm:text-sm">{t('storyInput.fromTextInjectHeroes')}</Label>
              <div data-testid="content-fromtext-inject-selector">
                <CharacterSelector
                  selectedCharacterIds={injectIds}
                  onSelectionChange={(ids) => { setInjectIds(ids); rebuildInject(ids, injectNames); }}
                  onNamesChange={(names) => { setInjectNames(names); rebuildInject(injectIds, names); }}
                  locale={locale}
                  recentCharacterIds={recentCharacterIds}
                />
              </div>
            </div>
          )}

          {/* Language + Age — shown ONLY for based_on ("Inspired by" writes a
              fresh story, so it needs an explicit output language + age). The
              recast_hero / incorporate modes keep the SOURCE language
              automatically; verbatim uses neither. */}
          {fromTextMode === 'based_on' && (
          <div className="grid gap-2 sm:gap-4 grid-cols-2">
            <div className="space-y-1">
              <Label className="text-xs sm:text-sm">{t('parameters.language')}</Label>
              <Select
                value={values.language}
                onValueChange={(v) => onChange((prev) => ({ ...prev, language: v, languageTouched: true }))}
              >
                <SelectTrigger className="w-full text-xs sm:text-sm h-10 px-2 sm:px-3" data-testid="content-language-select-text">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {LANGUAGES.map((lang) => (
                    <SelectItem key={lang} value={lang}>{t(`languages.${lang}`)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs sm:text-sm">{t('parameters.ageGroup')}</Label>
              <Select value={values.ageGroup} onValueChange={(v) => update('ageGroup', v)}>
                <SelectTrigger className="w-full text-xs sm:text-sm h-10 px-2 sm:px-3" data-testid="content-age-group-select-text">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {AGE_GROUPS.map((ag) => (
                    <SelectItem key={ag} value={ag}>{t(`ageGroups.${ag}`)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          )}

          {/* Target language — ONLY for translate: the story is translated into
              this output language, structure unchanged. */}
          {/* The language of the BOOK, asked in every mode that keeps the author's
              text. A parent whose text is Russian may want the story in Czech —
              and slicing a Russian text under a Czech label is the one answer that
              is simply wrong. Picking another language turns verbatim into
              translate, below. */}
          {(fromTextMode === 'translate' || fromTextMode === 'verbatim') && (
          <div className="space-y-1">
            <Label className="text-xs sm:text-sm">{t('storyInput.fromTextTargetLanguage')}</Label>
            <Select
              value={values.language}
              onValueChange={(v) => {
                // Asked for another language than the text is written in → it has
                // to be TRANSLATED. Slicing keeps the author's words, which is
                // exactly what must not happen here.
                const needsTranslation = !!detectedLanguage && v !== detectedLanguage;
                onChange((prev) => ({
                  ...prev,
                  language: v,
                  languageTouched: true,
                  fromTextMode:
                    needsTranslation
                      ? 'translate'
                      : fromTextMode === 'translate' ? 'verbatim' : fromTextMode,
                }));
              }}
            >
              <SelectTrigger className="w-full text-xs sm:text-sm h-10 px-2 sm:px-3" data-testid="content-language-select-text">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {LANGUAGES.map((lang) => (
                  <SelectItem key={lang} value={lang}>{t(`languages.${lang}`)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {detectedLanguage && detectedLanguage !== values.language && (
              <p className="text-xs text-muted-foreground" data-testid="from-text-translate-note">
                {/* Both names in the READER's language: the picker shows native
                    names ("Čeština"), which inside a Russian sentence reads as a
                    bug ("сказка будет на Čeština"). */}
                {t('storyInput.willTranslate', {
                  from: t(`storyInput.languageNames.${detectedLanguage}`),
                  to: t(`storyInput.languageNames.${values.language}`),
                })}
              </p>
            )}
          </div>
          )}

          {/* The heroes of a text the parent already wrote.
            *
            * Translating or keeping your own story used to be the one path with
            * no way to say "this Ника is MY Ника": the from-text branch has no
            * heroes step, and the hero controls live in the modes that rewrite
            * the text. So the story was created with no characters linked, the
            * illustrator invented a child, and the photo in the library was
            * never used. The names are matched across scripts, so a Russian
            * "Ника" finds the Czech translation's "Nika" by itself — the only
            * rule is that the character is named the way the text names them. */}
          {/* translate: the heroes are the source's (the model drops a hero pick) — no selection */}
          {fromTextMode === 'verbatim' && (
            <div className="space-y-2" data-testid="content-fromtext-own-heroes">
              <Label className="text-xs sm:text-sm">{t('storyInput.ownHeroesLabel')}</Label>
              <p className="text-[11px] text-muted-foreground">{t('storyInput.ownHeroesHint')}</p>
              <CharacterSelector
                selectedCharacterIds={values.characterIds ?? []}
                onSelectionChange={(ids) => onChange((prev) => ({ ...prev, characterIds: ids }))}
                locale={locale}
                recentCharacterIds={recentCharacterIds}
                maxCharacters={4}
              />
              <div className="space-y-1.5 rounded-md border border-dashed p-2" data-testid="content-fromtext-own-photohero-add">
                <p className="text-[11px] text-muted-foreground">{t('storyInput.photoHeroAddHint')}</p>
                {photoHeroes.map((h) => (
                  <div key={h.characterId} className="flex items-center gap-2">
                    {h.previewUrl && (
                      // eslint-disable-next-line @next/next/no-img-element
                      <img src={h.previewUrl} alt="" className="h-8 w-8 shrink-0 rounded object-cover" />
                    )}
                    <input
                      value={h.name}
                      onChange={(e) => setPhotoHeroName(h.characterId, e.target.value)}
                          onBlur={(e) => commitPhotoHeroName(h.characterId, e.target.value)}
                      placeholder={t('storyInput.photoHeroNamePlaceholder')}
                      className={cn(
                        'min-w-0 flex-1 rounded border bg-background px-2 py-1 text-xs',
                        !h.name.trim() && 'border-destructive',
                      )}
                      data-testid={`content-fromtext-own-photohero-name-${h.characterId}`}
                    />
                    <button
                      type="button"
                      onClick={() => removePhotoHero(h.characterId)}
                      className="shrink-0 text-[11px] text-muted-foreground hover:text-destructive"
                      aria-label={t('storyInput.photoHeroRemove')}
                    >
                      ✕
                    </button>
                  </div>
                ))}
                <PhotoConsentCheckbox
                  compact
                  checked={photoConsent}
                  onChange={(v) => { setPhotoConsent(v); setPhotoErr(null); }}
                />
                <label
                  className={cn(
                    'inline-flex items-center gap-0.5 rounded-full border border-dashed px-2 py-0.5 text-[10px]',
                    photoConsent
                      ? 'cursor-pointer text-muted-foreground hover:border-primary/50 hover:text-primary'
                      : 'cursor-not-allowed opacity-50 text-muted-foreground',
                  )}
                >
                  {t('storyInput.photoHeroAdd')}
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    disabled={photoBusy || !photoConsent}
                    onChange={(e) => { addPhotoHero(e.target.files?.[0]); e.currentTarget.value = ''; }}
                    data-testid="content-fromtext-own-photohero-file"
                  />
                </label>
                {photoBusy && <span className="ml-2 text-[10px] text-muted-foreground">…</span>}
                {photoErr && <p className="text-[10px] text-destructive">{photoErr}</p>}
              </div>
            </div>
          )}

          {/* Copyright gate — the paste must be the user's own text or public
              domain. Required to proceed (canGoNext checks fromTextTosAccepted).
              HIDDEN only when the source is one of OUR basis stories: the text is
              then not the user's paste and the affirmation does not apply.
              It used to be hidden in the heroes-first flow as well (basedOnLocked),
              where the gate still demanded it — a dead end: "Отметьте, что текст
              ваш" with nothing on the page to tick. */}
          {!values.basisStoryId && (
            <CheckboxField
              id="content-fromtext-tos"
              data-testid="content-fromtext-tos"
              checked={!!values.fromTextTosAccepted}
              onCheckedChange={(c) => onChange((prev) => ({ ...prev, fromTextTosAccepted: c }))}
              label={t('storyInput.fromTextTos')}
              finePrint
            />
          )}

          {/* Headless selector — owns the create sheet opened by the cast "+ create"
              chip; its list is hidden (the chips above ARE the picker). */}
          <div className="hidden">
            <CharacterSelector
              ref={castSelectorRef}
              selectedCharacterIds={values.characterIds}
              onSelectionChange={(ids) => onChange((prev) => ({ ...prev, characterIds: ids }))}
              locale={locale}
              recentCharacterIds={recentCharacterIds}
            />
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
